import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CampaignJobStatusDataRow,
  CampaignEventData,
  CliFileStatus,
  BatchStatus,
  FetchCampaignJobStatusDataRequest,
  FetchCampaignJobStatusDataResponse,
} from "@/modules/administration/features/Stochastic/CampaignJobStatus/api/fetchCampaignJobStatusData/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchStochasticClientMailData } from "@/modules/administration/features/Stochastic/CampaignJobStatus/api/fetchCampaignJobStatusData/fetchCampaignJobStatusDataApi";

interface CampaignJobStatusDataState {
  campaignJobStatusDataRows: CampaignJobStatusDataRow[];
  campaignWeekEventData: CampaignEventData | null;
  cliFileStatus: CliFileStatus;
  batchStatuses: BatchStatus[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: CampaignJobStatusDataState = {
  campaignJobStatusDataRows: [],
  campaignWeekEventData: null,
  cliFileStatus: {
    isCLIFileCanBeDownloaded: false,
    message: null,
    hasPendingBatches: false,
  },
  batchStatuses: [],
  totalCount: 0,
  loading: false,
  error: null,
};

const campaignJobStatusDataSlice = createSlice({
  name: "campaignJobStatusData",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setMailData: (
      state,
      action: PayloadAction<{
        campaignJobStatusDataRows: CampaignJobStatusDataRow[];
        campaignWeekEventData: CampaignEventData | null;
        cliFileStatus: CliFileStatus;
        batchStatuses: BatchStatus[];
        totalCount: number;
      }>,
    ) => {
      state.campaignJobStatusDataRows =
        action.payload.campaignJobStatusDataRows;
      state.campaignWeekEventData = action.payload.campaignWeekEventData;
      state.cliFileStatus = action.payload.cliFileStatus;
      state.batchStatuses = action.payload.batchStatuses;
      state.totalCount = action.payload.totalCount;
    },
  },
});

export const { setLoading, setError, setMailData } =
  campaignJobStatusDataSlice.actions;

export const fetchStochasticMailDataAction =
  (requestParams: FetchCampaignJobStatusDataRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: FetchCampaignJobStatusDataResponse =
        await fetchStochasticClientMailData(requestParams);

      dispatch(
        setMailData({
          campaignJobStatusDataRows: response.data.campaignJobStatusDataRows,
          campaignWeekEventData: response.data.campaignWeekEventData,
          cliFileStatus: response.data.cliFileStatus,
          batchStatuses: response.data.batchStatuses,
          totalCount: response.meta?.totalCount ?? 0,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch campaign job status data"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export default campaignJobStatusDataSlice.reducer;
